import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Invitation.css';
import restApi from '../../services/restApi';

// Must match INVITE_TOKEN in parsroll-back/src/controllers/InvitationController.ts
const INVITE_TOKEN = 'bahare-va-hamid-9384';

type Step = 'ask' | 'thanks' | 'food' | 'dealAccepted' | 'day' | 'final';

const FOOD_OPTIONS: { key: string; label: string; icon: string }[] = [
  { key: 'iranian', label: 'Iranian Cuisine', icon: '🍚' },
  { key: 'eastAsian', label: 'East Asian', icon: '🍜' },
  { key: 'salads', label: 'Salads', icon: '🥗' },
  { key: 'pizza', label: 'Pizza', icon: '🍕' },
  { key: 'burger', label: 'Burger', icon: '🍔' },
  { key: 'seafood', label: 'Sea Food', icon: '🦐' },
];

const DAY_OPTIONS: { key: string; label: string }[] = [
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
];

const HeartIcon = () => (
  <svg className="inviteFinalHeart" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 21s-7.5-4.6-10.2-9.1C.2 8.7 1.4 5 5 4c2.2-.6 4.2.5 5.4 2.3.4.6 1.3.6 1.7 0C13.3 4.5 15.3 3.4 17.5 4c3.6 1 4.8 4.7 3.2 7.9C19.5 16.4 12 21 12 21z"
      fill="currentColor"
    />
  </svg>
);

const DODGE_RADIUS = 120;

const Invitation: React.FC = () => {
  const [step, setStep] = useState<Step>('ask');
  const [food, setFood] = useState<{ key: string; label: string } | null>(null);
  const [day, setDay] = useState<{ key: string; label: string } | null>(null);
  const [noStyle, setNoStyle] = useState<React.CSSProperties>({});
  const sentRef = useRef(false);
  const noRef = useRef<HTMLButtonElement>(null);

  const sparkles = useMemo(
    () =>
      Array.from({ length: 22 }).map((_, i) => ({
        left: Math.random() * 100,
        size: 3 + Math.random() * 5,
        delay: Math.random() * 10,
        duration: 9 + Math.random() * 10,
        key: i,
      })),
    [],
  );

  const sendConfirmation = async (foodKey: string, dayKey: string) => {
    if (sentRef.current) return;
    sentRef.current = true;
    try {
      await restApi(`${process.env.REACT_APP_BASE_URL}/invitation/confirm`, false).post({
        food: foodKey,
        day: dayKey,
        token: INVITE_TOKEN,
      });
    } catch (e) {
      // Silent — this shouldn't interrupt her moment even if the SMS call fails.
      console.log(e);
    }
  };

  const chooseDay = (opt: { key: string; label: string }) => {
    setDay(opt);
    setStep('final');
    sendConfirmation(food?.key || '', opt.key);
  };

  const dodgeNo = (fromX?: number, fromY?: number) => {
    const btn = noRef.current;
    if (!btn) return;
    const w = btn.offsetWidth || 110;
    const h = btn.offsetHeight || 48;
    const pad = 16;
    const maxLeft = Math.max(window.innerWidth - w - pad, pad);
    const maxTop = Math.max(window.innerHeight - h - pad, pad);

    let left = pad + Math.random() * (maxLeft - pad);
    let top = pad + Math.random() * (maxTop - pad);

    if (fromX !== undefined && fromY !== undefined) {
      let attempts = 0;
      while (Math.hypot(left - fromX, top - fromY) < DODGE_RADIUS * 1.5 && attempts < 10) {
        left = pad + Math.random() * (maxLeft - pad);
        top = pad + Math.random() * (maxTop - pad);
        attempts++;
      }
    }

    setNoStyle({ position: 'fixed', left, top, margin: 0, zIndex: 60 });
  };

  // Once she gets near the "no" button (mouse or touch), it teleports away.
  useEffect(() => {
    if (step !== 'ask') return undefined;

    const handleMove = (clientX: number, clientY: number) => {
      const btn = noRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const dist = Math.hypot(clientX - (rect.left + rect.width / 2), clientY - (rect.top + rect.height / 2));
      if (dist < DODGE_RADIUS) {
        dodgeNo(clientX, clientY);
      }
    };

    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) handleMove(t.clientX, t.clientY);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [step]);

  useEffect(() => {
    if (step === 'thanks') {
      const t = setTimeout(() => setStep('food'), 2000);
      return () => clearTimeout(t);
    }
    if (step === 'dealAccepted') {
      const t = setTimeout(() => setStep('day'), 2000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step]);

  return (
    <div className="inviteRoot">
      {sparkles.map((s) => (
        <span
          key={s.key}
          className="inviteSparkle"
          style={{
            left: `${s.left}%`,
            bottom: '-20px',
            width: s.size,
            height: s.size,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}

      <div className="inviteCard">
        {step === 'ask' && (
          <div key={step} className="inviteStepContent">
            <p className="inviteEyebrow">a question, for you</p>
            <h1 className="inviteHeading">
              Hello there, would you forgive me and go out with me? :((
            </h1>
            <div className="inviteDivider" />
            <div className="inviteChoiceRow">
              <button className="inviteBtn" onClick={() => setStep('thanks')}>
                yes
              </button>
              <button
                ref={noRef}
                className="inviteBtn inviteBtnGhost inviteBtnNo"
                style={noStyle}
                tabIndex={-1}
                onMouseEnter={() => dodgeNo()}
                onTouchStart={(e) => {
                  e.preventDefault();
                  const t = e.touches[0];
                  dodgeNo(t?.clientX, t?.clientY);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  dodgeNo();
                }}
              >
                no
              </button>
            </div>
          </div>
        )}

        {step === 'thanks' && (
          <div key={step} className="inviteStepContent">
            <h1 className="inviteHeading">thanks babyyy</h1>
          </div>
        )}

        {step === 'food' && (
          <div key={step} className="inviteStepContent">
            <h1 className="inviteHeading">what are we feeling?</h1>
            <div className="inviteDivider" />
            <div className="inviteOptionsGrid">
              {FOOD_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  className="inviteOptionCard"
                  onClick={() => {
                    setFood(opt);
                    setStep('dealAccepted');
                  }}
                >
                  <span className="inviteOptionIcon">{opt.icon}</span>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'dealAccepted' && (
          <div key={step} className="inviteStepContent">
            <p className="inviteEyebrow">{food?.label}</p>
            <h1 className="inviteHeading">u got the deal</h1>
          </div>
        )}

        {step === 'day' && (
          <div key={step} className="inviteStepContent">
            <h1 className="inviteHeading">when are u free this week azizam?</h1>
            <div className="inviteDivider" />
            <div className="inviteDaysGrid">
              {DAY_OPTIONS.map((opt) => (
                <button key={opt.key} className="inviteOptionCard" onClick={() => chooseDay(opt)}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'final' && (
          <div key={step} className="inviteStepContent">
            <HeartIcon />
            <h1 className="inviteHeading">
              okaaay ily and i will pick u up on <span className="inviteFinalWord">{day?.label}</span> to
              have some <span className="inviteFinalWord">{food?.label}</span>. boos
            </h1>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invitation;
